/**
 * As peças que as quatro telas de "outros projetos" dividem.
 *
 * Torques, relés, tabelas de valores e planos têm a mesma anatomia — uma lista
 * de mestres à esquerda, o detalhe à direita, coleções filhas editáveis dentro
 * dele. Escrever isso quatro vezes daria quatro lugares para o mesmo conserto.
 */

import { Children, Fragment, cloneElement, isValidElement, useEffect, useId, useMemo, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { obter } from "@/api/cliente"
import { BUSCA_ESPERA_MS, catalogoPorProjeto } from "@/api/query"
import { ErroDaApi, ErroDeTrava } from "@/api/erros"
import { AlcaDeLargura, usarLarguraArrastavel } from "@/kit/LarguraArrastavel"
import { SeletorComBusca } from "@/kit/SeletorComBusca"
import { BotaoDeOrdem, type OrdemDaLista } from "@/kit/ordemDaLista"

/** Quantas linhas desenhar antes de resumir — o corte do resto do sistema. */
export const DESENHADAS = 200

export function mensagem(e: unknown): string {
  if (e instanceof ErroDeTrava) return e.message
  if (e instanceof ErroDaApi) return e.message
  return "Não foi possível concluir."
}

// ------------------------------------------------------------------ catálogos

type Bruto = { id: number; nome?: string; modelo?: string; descricao?: string; marcaId?: number }

/**
 * Resolve id para nome nos três catálogos que estes módulos citam.
 *
 * Sem isso a tela mostra "motor 23155" e "sistema 68" — números que ninguém
 * reconhece. Os três carregam uma vez por sessão e o resto é memória.
 */
export function usarNomes() {
  const marcas = useQuery({
    queryKey: ["catalogo-conteudo", "marcas"] as const,
    ...catalogoPorProjeto,
    queryFn: () => obter<Bruto[]>("api/marcas", {}),
  })
  const veiculos = useQuery({
    queryKey: ["catalogo-conteudo", "veiculos"] as const,
    ...catalogoPorProjeto,
    queryFn: () => obter<Bruto[]>("api/veiculos", {}),
  })
  const motores = useQuery({
    queryKey: ["catalogo-conteudo", "motores"] as const,
    ...catalogoPorProjeto,
    queryFn: () => obter<Bruto[]>("api/motores", {}),
  })
  const sistemas = useQuery({
    queryKey: ["catalogo-conteudo", "sistemas"] as const,
    ...catalogoPorProjeto,
    queryFn: () => obter<Bruto[]>("api/sistemas", {}),
  })

  return useMemo(() => {
    const marca = new Map((marcas.data ?? []).map((m) => [m.id, m.nome ?? ""]))
    const comMarca = (x: Bruto) =>
      [marca.get(x.marcaId ?? -1), x.nome ?? x.modelo]
        .filter((p) => p && p.trim())
        // O cadastro repete marca dentro do nome em muitos registros.
        .filter((p, i, todas) => todas.findIndex((o) => o!.toLowerCase() === p!.toLowerCase()) === i)
        .join(" ")

    return {
      marca,
      veiculo: new Map((veiculos.data ?? []).map((v) => [v.id, comMarca(v)])),
      /** A marca de cada veículo — para o filtro de modelo seguir o de marca. */
      marcaDoVeiculo: new Map((veiculos.data ?? []).map((v) => [v.id, v.marcaId ?? 0])),
      motor: new Map((motores.data ?? []).map((m) => [m.id, comMarca(m)])),
      sistema: new Map((sistemas.data ?? []).map((s) => [s.id, s.nome ?? ""])),
      carregando: marcas.isPending || veiculos.isPending || motores.isPending || sistemas.isPending,
    }
  }, [marcas.data, marcas.isPending, veiculos.data, veiculos.isPending,
      motores.data, motores.isPending, sistemas.data, sistemas.isPending])
}

export type Nomes = ReturnType<typeof usarNomes>

/**
 * A faixa de anos de um vínculo de veículo está ao contrário. Ano vazio (0) é "sem
 * limite" e não entra na comparação — é a mesma regra dos validadores da API, checada
 * antes para o botão não mandar o que vai voltar com 400.
 */
export function anosInvertidos({ anoInicial, anoFinal }: { anoInicial: number; anoFinal: number }) {
  return anoInicial > 0 && anoFinal > 0 && anoInicial > anoFinal
}

/** Um id sem nome no catálogo é registro apagado que alguém ainda cita. */
export function nomeOu(mapa: Map<number, string>, id: number | undefined | null, prefixo: string): string {
  if (!id) return "—"
  const achado = mapa.get(id)
  return achado && achado.trim() ? achado : `${prefixo} ${id}`
}

// ------------------------------------------------------------------ estrutura

export function ColunaDeLista({ largura = 320, children }: {
  largura?: number
  children: React.ReactNode
}) {
  return (
    <div style={{
      flex: `0 0 ${largura}px`, width: largura, minWidth: 0, overflowY: "auto",
      borderRight: "1px solid var(--line2)", padding: 12,
    }}>
      {children}
    </div>
  )
}

/**
 * Coluna de lista que **recolhe e muda de largura** — a das telas em três níveis.
 *
 * Projeto, caixa e itens lado a lado comiam 630px antes da tabela, que é onde se
 * trabalha. Recolhida, a coluna vira uma tira de 34px com o título em pé; aberta,
 * a borda direita arrasta (ou ← → no teclado, duplo clique volta ao padrão).
 *
 * Largura e estado ficam no navegador, por `chave`: quem recolheu a lista de
 * projetos não quer abrir de novo a cada visita. Sem armazenamento (aba anônima),
 * funciona igual e só esquece. O arrastar é o `kit/LarguraArrastavel`, o mesmo das
 * barras laterais do diagrama.
 */
export function ColunaRecolhivel({ chave, titulo, detalhe, topo, largura = 320, minima = 200, maxima = 640, children }: {
  chave: string
  titulo: string
  detalhe?: React.ReactNode
  /** Fica fixo entre o cabeçalho e a lista, sem rolar com ela — filtro e ordem da lista. */
  topo?: React.ReactNode
  largura?: number
  minima?: number
  maxima?: number
  children: React.ReactNode
}) {
  const guardado = useMemo(() => lerColuna(chave), [chave])
  // A largura e o arrastar são os do kit; a largura que já estava guardada aqui vale de início.
  const larguraDaColuna = usarLarguraArrastavel({
    chave: `coluna-${chave}`, padrao: largura, minimo: minima, maximo: maxima, inicial: guardado?.largura,
  })
  const atual = larguraDaColuna.largura
  const arrastando = larguraDaColuna.arrastando
  const [recolhida, setRecolhida] = useState(guardado?.recolhida ?? false)

  useEffect(() => { gravarColuna(chave, { largura: atual, recolhida }) }, [chave, atual, recolhida])

  if (recolhida) {
    return (
      <button
        onClick={() => setRecolhida(false)}
        title={`Mostrar ${titulo.toLowerCase()}`}
        aria-label={`Mostrar ${titulo.toLowerCase()}`}
        aria-expanded={false}
        style={{
          flex: "0 0 34px", width: 34, display: "flex", flexDirection: "column", alignItems: "center",
          gap: 10, padding: "12px 0", border: 0, borderRight: "1px solid var(--line2)",
          background: "var(--surface2, transparent)", cursor: "pointer", color: "var(--txt2)",
        }}
      >
        <span aria-hidden style={{ fontSize: 13, lineHeight: 1 }}>»</span>
        <span style={{
          writingMode: "vertical-rl", fontSize: 11, fontWeight: 600, letterSpacing: ".04em",
          textTransform: "uppercase", color: "var(--txt3)", whiteSpace: "nowrap",
          overflow: "hidden", textOverflow: "ellipsis", maxHeight: "100%",
        }}>
          {titulo}
        </span>
      </button>
    )
  }

  return (
    <div style={{
      flex: `0 0 ${atual}px`, width: atual, minWidth: 0, position: "relative",
      display: "flex", flexDirection: "column", borderRight: "1px solid var(--line2)",
      userSelect: arrastando ? "none" : undefined,
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 8, padding: "8px 8px 6px 12px",
        borderBottom: "1px solid var(--line2)",
      }}>
        <h3 style={{
          margin: 0, fontSize: 11, fontWeight: 600, letterSpacing: ".04em", textTransform: "uppercase",
          color: "var(--txt3)", whiteSpace: "nowrap",
        }}>
          {titulo}
        </h3>
        {detalhe && (
          <span style={{
            fontSize: 11, color: "var(--txt2)", minWidth: 0, overflow: "hidden",
            textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {detalhe}
          </span>
        )}
        <button
          onClick={() => setRecolhida(true)}
          title={`Recolher ${titulo.toLowerCase()}`}
          aria-label={`Recolher ${titulo.toLowerCase()}`}
          aria-expanded
          style={{
            marginLeft: "auto", flex: "0 0 auto", border: "1px solid var(--line2)", borderRadius: 5,
            background: "var(--surface)", color: "var(--txt2)", cursor: "pointer",
            fontSize: 12, lineHeight: 1, padding: "3px 6px",
          }}
        >
          «
        </button>
      </div>

      {topo && (
        <div style={{
          display: "flex", flexDirection: "column", gap: 6, padding: "10px 12px",
          borderBottom: "1px solid var(--line2)",
        }}>
          {topo}
        </div>
      )}

      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 12 }}>{children}</div>

      <AlcaDeLargura {...larguraDaColuna} borda="direita" rotulo={`Largura de ${titulo.toLowerCase()}`} />
    </div>
  )
}

type EstadoDaColuna = { largura: number; recolhida: boolean }

function lerColuna(chave: string): EstadoDaColuna | null {
  try {
    const bruto = localStorage.getItem(`coluna:${chave}`)
    if (!bruto) return null
    const v = JSON.parse(bruto) as Partial<EstadoDaColuna>
    return typeof v.largura === "number" ? { largura: v.largura, recolhida: !!v.recolhida } : null
  } catch {
    return null
  }
}

function gravarColuna(chave: string, estado: EstadoDaColuna) {
  try { localStorage.setItem(`coluna:${chave}`, JSON.stringify(estado)) } catch { /* sem armazenamento: só esquece */ }
}

/**
 * Janela por cima da tela, que **não desmonta o que está embaixo**.
 *
 * A inclusão de projeto trocava o painel inteiro: cancelar devolvia a tela vazia, sem
 * o projeto, a caixa e as centrais abertas que estavam ali. Por cima, fechar é só
 * fechar. Esc e clique fora fecham.
 */
export function Janela({ titulo, aoFechar, largura = 520, children }: {
  titulo: string
  aoFechar: () => void
  largura?: number
  children: React.ReactNode
}) {
  useEffect(() => {
    const tecla = (e: KeyboardEvent) => { if (e.key === "Escape") aoFechar() }
    window.addEventListener("keydown", tecla)
    return () => window.removeEventListener("keydown", tecla)
  }, [aoFechar])

  return (
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget) aoFechar() }}
      style={{
        position: "fixed", inset: 0, zIndex: 50, background: "rgba(15, 18, 25, .38)",
        display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "10vh 16px 16px",
      }}
    >
      <div role="dialog" aria-modal="true" aria-label={titulo} style={{
        width: largura, maxWidth: "100%", maxHeight: "80vh", overflowY: "auto",
        background: "var(--surface)", color: "var(--txt)", borderRadius: 10,
        border: "1px solid var(--line2)", boxShadow: "var(--sombra, 0 12px 32px rgba(0,0,0,.2))",
        padding: 18,
      }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 12 }}>
          <b style={{ fontSize: 13.5 }}>{titulo}</b>
          <button onClick={aoFechar} aria-label="Fechar" style={{
            marginLeft: "auto", border: 0, background: "transparent", cursor: "pointer",
            color: "var(--txt3)", fontSize: 16, lineHeight: 1,
          }}>×</button>
        </div>
        {children}
      </div>
    </div>
  )
}

/** Uma etiqueta pequena — posição, ano — com um × opcional para tirar. */
export function Etiqueta({ children, aoTirar, rotuloTirar }: {
  children: React.ReactNode
  aoTirar?: () => void
  rotuloTirar?: string
}) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4, maxWidth: "100%",
      padding: "2px 7px", borderRadius: 10, fontSize: 11, lineHeight: 1.5,
      background: "var(--acao-soft)", color: "var(--acao-on-soft)",
    }}>
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{children}</span>
      {aoTirar && (
        <button onClick={aoTirar} aria-label={rotuloTirar ?? "Tirar"} style={{
          border: 0, background: "transparent", cursor: "pointer", padding: 0,
          color: "inherit", fontSize: 12, lineHeight: 1, opacity: 0.7,
        }}>×</button>
      )}
    </span>
  )
}

export function ItemDaLista({ ativo, aoClicar, titulo, detalhe, marcado, recente }: {
  ativo: boolean
  aoClicar: () => void
  titulo: React.ReactNode
  detalhe?: React.ReactNode
  /** Publicado, validado — o que o módulo considerar digno de um ponto. */
  marcado?: boolean
  /** Acabou de ser incluído: realce que esmaece. Ver `kit/ordemDaLista`. */
  recente?: boolean
}) {
  return (
    <button
      onClick={aoClicar}
      data-recem-incluido={recente ? "" : undefined}
      style={{
        display: "block", width: "100%", textAlign: "left", marginBottom: 4,
        border: "1px solid " + (ativo ? "var(--acao)" : "var(--line2)"),
        borderRadius: 6, padding: "6px 9px", cursor: "pointer",
        background: ativo ? "var(--acao-soft)" : "var(--surface)",
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
        {marcado && (
          <span title="Publicado" style={{
            flex: "0 0 auto", width: 6, height: 6, borderRadius: 3,
            background: "var(--acao)", display: "inline-block",
          }} />
        )}
        <span style={{
          fontSize: 12, fontWeight: 500, overflow: "hidden",
          textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {titulo}
        </span>
      </div>
      {detalhe && (
        <div style={{ marginTop: 2, fontSize: 10.5, color: "var(--txt3)" }}>{detalhe}</div>
      )}
    </button>
  )
}

export function Painel({ children }: { children: React.ReactNode }) {
  return <div style={{ flex: 1, minWidth: 0, overflowY: "auto", padding: 16 }}>{children}</div>
}

export function Secao({ titulo, contagem, acao, children }: {
  titulo: string
  contagem?: React.ReactNode
  acao?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <section style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 9, marginBottom: 7 }}>
        <h3 style={{
          margin: 0, fontSize: 11, fontWeight: 600, letterSpacing: ".04em",
          textTransform: "uppercase", color: "var(--txt3)",
        }}>
          {titulo}
        </h3>
        {contagem && <span style={{ fontSize: 11, color: "var(--txt2)" }}>{contagem}</span>}
        {acao && <span style={{ marginLeft: "auto" }}>{acao}</span>}
      </div>
      {children}
    </section>
  )
}

export type ColunaSimples<T> = {
  chave: string
  rotulo: string
  largura?: number
  numero?: boolean
  render?: (item: T) => React.ReactNode
}

/**
 * Tabela de uma coleção filha. Desenha até 200 e diz quanto escondeu — um grupo
 * de manutenção chega a 15.283 itens numa resposta só.
 */
export function TabelaSimples<T extends { id: number }>({ colunas, itens, aoAbrir, selecionado, vazio, recente, abaixoDe }: {
  colunas: ColunaSimples<T>[]
  itens: T[]
  aoAbrir?: (item: T) => void
  selecionado?: number | null
  vazio: string
  /** As linhas que acabaram de entrar: realce que esmaece. Ver `kit/ordemDaLista`. */
  recente?: (id: number) => boolean
  /**
   * Uma faixa colada embaixo da linha, da largura da tabela — a confirmação de remover
   * aparece onde a pessoa clicou, e não no pé de uma tabela de 200 linhas.
   */
  abaixoDe?: (item: T) => React.ReactNode
}) {
  const [busca, setBusca] = useState("")

  const achados = useMemo(() => {
    const t = busca.trim().toLowerCase()
    if (!t) return itens
    return itens.filter((i) =>
      colunas.some((c) => String((i as Record<string, unknown>)[c.chave] ?? "").toLowerCase().includes(t)))
  }, [itens, colunas, busca])

  const corte = achados.slice(0, DESENHADAS)
  // As recém-incluídas vão para o fim da lista, e numa tabela de mais de 200 linhas
  // cairiam fora do corte — justamente as que a pessoa quer ver.
  const alemDoCorte = recente ? achados.slice(DESENHADAS).filter((i) => recente(i.id)) : []
  const desenhados = alemDoCorte.length > 0 ? [...corte, ...alemDoCorte] : corte

  if (itens.length === 0) {
    return <p style={{ fontSize: 12, color: "var(--txt3)" }}>{vazio}</p>
  }

  return (
    <>
      {itens.length > 25 && (
        <input
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="filtrar…"
          style={{
            marginBottom: 5, padding: "3px 8px", fontSize: 11, width: 220,
            border: "1px solid var(--line)", borderRadius: 5,
            background: "var(--surface)", color: "var(--txt)",
          }}
        />
      )}

      <div style={{ overflowX: "auto", border: "1px solid var(--line2)", borderRadius: 6 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11.5 }}>
          <thead>
            <tr>
              {colunas.map((c) => (
                <th key={c.chave} style={{
                  textAlign: c.numero ? "right" : "left", padding: "5px 8px",
                  fontSize: 10.5, fontWeight: 600, color: "var(--txt2)",
                  borderBottom: "1px solid var(--line)", whiteSpace: "nowrap", width: c.largura,
                }}>
                  {c.rotulo}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {desenhados.map((i) => {
              const faixa = abaixoDe?.(i)
              return (
              <Fragment key={i.id}>
              <tr
                data-recem-incluido={recente?.(i.id) ? "" : undefined}
                onClick={() => aoAbrir?.(i)}
                style={{
                  borderBottom: "1px solid var(--line2)",
                  cursor: aoAbrir ? "pointer" : "default",
                  background: selecionado === i.id ? "var(--acao-soft)" : undefined,
                }}
              >
                {colunas.map((c) => {
                  const cru = (i as Record<string, unknown>)[c.chave]
                  return (
                    <td key={c.chave} style={{
                      padding: "4px 8px",
                      textAlign: c.numero ? "right" : "left",
                      fontVariantNumeric: c.numero ? "tabular-nums" : undefined,
                      maxWidth: c.largura ?? 280,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }} title={String(cru ?? "")}>
                      {c.render ? c.render(i) : celula(cru, c.numero)}
                    </td>
                  )
                })}
              </tr>
              {faixa && (
                <tr style={{ borderBottom: "1px solid var(--line2)" }}>
                  <td colSpan={colunas.length} style={{ padding: "4px 8px 6px" }}>{faixa}</td>
                </tr>
              )}
              </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>

      {achados.length > desenhados.length && (
        <p style={{ margin: "4px 0 0", fontSize: 10.5, color: "var(--txt3)" }}>
          {achados.length.toLocaleString("pt-BR")} linhas; mostrando {DESENHADAS}.
        </p>
      )}
    </>
  )
}

function celula(v: unknown, numero?: boolean): string {
  if (v === null || v === undefined || v === "") return "—"
  if (typeof v === "boolean") return v ? "sim" : "não"
  if (typeof v === "number") {
    // Ano não leva separador: "2.012" não é ano nenhum.
    return numero && (v < 1000 || v > 2999) ? v.toLocaleString("pt-BR") : String(v)
  }
  const t = String(v)
  const data = /^(\d{4})-(\d{2})-(\d{2})T/.exec(t)
  return data ? `${data[3]}/${data[2]}/${data[1]}` : t
}

// ------------------------------------------------------------------ formulário

/**
 * Rótulo, campo e ajuda.
 *
 * **O rótulo estava solto.** Era um `<label>` sem `htmlFor`, irmão do input — bonito na
 * tela e invisível para leitor de tela: todos os campos destas telas eram "caixa de texto
 * sem nome". Descobri porque o primeiro teste do front não achou o campo "Título" pelo
 * nome, que é a mesma pergunta que a tecnologia assistiva faz.
 *
 * A amarração clona o filho para pôr um `id`, e só quando ele é um elemento de formulário
 * simples (`input`, `select`, `textarea`) sem id próprio. Componentes (o `Escolha`, que é
 * um seletor com busca) ou casos com `<label>` interno seguem como estavam — esses
 * declaram `aria-label` por conta.
 */
export function Campo({ rotulo, children, ajuda }: {
  rotulo: string
  children: React.ReactNode
  ajuda?: string
}) {
  const id = useId()
  // `ReactElement<{ id?: string }>` e nao `ReactElement` cru: sem o tipo das props, o
  // `cloneElement` recusa `id` como propriedade desconhecida.
  const unico = Children.count(children) === 1
    ? (children as React.ReactElement<{ id?: string }>)
    : null
  const simples = !!unico && isValidElement(unico)
    && typeof unico.type === "string"
    && ["input", "select", "textarea"].includes(unico.type)
    && !unico.props.id

  return (
    <div style={{ marginBottom: 9, flex: 1, minWidth: 130 }}>
      <label htmlFor={simples ? id : undefined}
             style={{ display: "block", fontSize: 10.5, color: "var(--txt2)", marginBottom: 3 }}>
        {rotulo}
      </label>
      {simples && unico ? cloneElement(unico, { id }) : children}
      {ajuda && <p style={{ margin: "2px 0 0", fontSize: 10, color: "var(--txt3)" }}>{ajuda}</p>}
    </div>
  )
}

export const estiloCampo: React.CSSProperties = {
  width: "100%", padding: "5px 7px", fontSize: 12,
  border: "1px solid var(--line)", borderRadius: 5,
  background: "var(--surface)", color: "var(--txt)",
}

export function Botao({ children, aoClicar, destaque, desligado, ocupado, titulo, envia }: {
  children: React.ReactNode
  aoClicar: () => void
  destaque?: boolean
  desligado?: boolean
  ocupado?: boolean
  titulo?: string
  /**
   * Este botão é o que o Enter aciona dentro de um `<form>`.
   *
   * Sem isto o botão é `type="button"`, e é de propósito: o padrão do HTML é
   * `submit`, então um "Limpar" ao lado de um "Procurar" dentro do mesmo form
   * dispara o `onSubmit` depois do próprio `onClick` — e o form reaplica o
   * filtro que o Limpar acabou de apagar, com o valor da renderização anterior.
   */
  envia?: boolean
}) {
  const inativo = desligado || ocupado
  return (
    <button
      type={envia ? "submit" : "button"}
      onClick={aoClicar}
      disabled={inativo}
      title={titulo}
      style={{
        border: "1px solid " + (destaque && !inativo ? "var(--acao)" : "var(--line)"),
        borderRadius: 6, padding: "4px 11px", fontSize: 12,
        cursor: inativo ? "default" : "pointer",
        background: destaque && !inativo ? "var(--acao)" : "var(--surface)",
        color: destaque && !inativo ? "#fff" : inativo ? "var(--txt3)" : "var(--txt)",
      }}
    >
      {children}
    </button>
  )
}

/**
 * Escolha num catálogo grande — sistemas são 1.663, motores 1.395.
 *
 * **Um campo só, que é a escolha e o filtro** (o `SeletorComBusca` do kit). Até
 * 26/10/2026 era um input de filtro em cima de um `<select>` nativo: quem digitava
 * "ecoboost 2.0" não achava "2.0 16V L4 Ecoboost" (o filtro exigia o trecho na mesma
 * ordem, com o mesmo acento) e não via o resultado até abrir o select — "Motorização
 * não dá pra filtrar e encontrar", veio da equipe. Agora cada palavra casa em qualquer
 * posição, sem caixa e sem acento, e a lista aparece embaixo enquanto se digita.
 *
 * **Desenha todas as opções**, sem corte. Até 13/10/2026 só as 200 primeiras em ordem
 * alfabética entravam, e a lista de 1.201 veículos acabava no "BYD": quem rolava não
 * achava o carro e concluía que ele não estava cadastrado.
 */
export function Escolha({ rotulo, mapa, valor, aoMudar, prefixo }: {
  rotulo: string
  mapa: Map<number, string>
  valor: number
  aoMudar: (v: number) => void
  prefixo: string
}) {
  const opcoes = useMemo(() => {
    const todas = [...mapa.entries()]
      .filter(([id, nome]) => id > 0 && nome.trim())
      .sort((a, b) => a[1].localeCompare(b[1], "pt-BR"))
      .map(([id, nome]) => ({ id, rotulo: nome }))
    // O escolhido tem de continuar conhecido mesmo sem nome no catálogo (registro apagado
    // que alguém ainda cita), senão o campo mostra vazio e parece que a escolha se perdeu.
    if (valor > 0 && !mapa.get(valor)?.trim()) todas.unshift({ id: valor, rotulo: nomeOu(mapa, valor, prefixo) })
    return todas
  }, [mapa, valor, prefixo])

  return (
    <Campo rotulo={rotulo}>
      {/* `rotuloAcessivel`: o `Campo` só amarra o rótulo a input/select/textarea crus. */}
      <SeletorComBusca
        itens={opcoes}
        valor={valor}
        aoMudar={aoMudar}
        rotuloAcessivel={rotulo}
        carregando={mapa.size === 0}
        placeholder={`Digite para procurar ${prefixo}…`}
        maximoDesenhado={Number.POSITIVE_INFINITY}
      />
    </Campo>
  )
}

/**
 * O filtro de uma lista de coluna, com a ordem ao lado — o topo da `ColunaRecolhivel`.
 *
 * Relés e fusíveis estreou o desenho (filtro e ordem moram com a lista que eles mudam,
 * não na faixa do topo); este é o mesmo para todas as telas da família. Sem `aoMudarOrdem`,
 * só o filtro.
 */
export function FiltroDaLista({ valor, aoMudar, rotulo, placeholder, ordem, aoMudarOrdem }: {
  valor: string
  aoMudar: (v: string) => void
  /** Nome para leitor de tela: "Filtrar projetos". */
  rotulo: string
  placeholder?: string
  ordem?: OrdemDaLista
  aoMudarOrdem?: (o: OrdemDaLista) => void
}) {
  return (
    <div style={{ display: "flex", gap: 6 }}>
      <input
        value={valor}
        onChange={(e) => aoMudar(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Escape" && valor) { e.stopPropagation(); aoMudar("") } }}
        placeholder={placeholder ?? `${rotulo}…`}
        aria-label={rotulo}
        style={{ ...estiloCampo, flex: 1, minWidth: 0, width: "auto" }}
      />
      {ordem && aoMudarOrdem && <BotaoDeOrdem valor={ordem} aoMudar={aoMudarOrdem} />}
    </div>
  )
}

/** O termo digitado, depois que a digitação para — para a busca que vai ao servidor. */
export function usarTermoAdiado(texto: string): string {
  const [termo, setTermo] = useState(texto.trim())
  useEffect(() => {
    const t = setTimeout(() => setTermo(texto.trim()), BUSCA_ESPERA_MS)
    return () => clearTimeout(t)
  }, [texto])
  return termo
}

/** Anterior / próxima no pé de uma lista paginada pela API. */
export function Paginacao({ pagina, aoMudar, temProxima, totalDePaginas }: {
  pagina: number
  aoMudar: (p: number) => void
  temProxima: boolean
  /** Quando a API diz o total; sem ele, só o número da página. */
  totalDePaginas?: number
}) {
  if (pagina <= 1 && !temProxima) return null
  return (
    <div style={{ display: "flex", gap: 6, alignItems: "center", justifyContent: "center", marginTop: 8 }}>
      <Botao aoClicar={() => aoMudar(Math.max(1, pagina - 1))} desligado={pagina <= 1}>Anterior</Botao>
      <span style={{ fontSize: 11, color: "var(--txt2)" }}>
        página {pagina}{totalDePaginas ? ` de ${totalDePaginas.toLocaleString("pt-BR")}` : ""}
      </span>
      <Botao aoClicar={() => aoMudar(pagina + 1)} desligado={!temProxima}>Próxima</Botao>
    </div>
  )
}

export function Linha({ children }: { children: React.ReactNode }) {
  return <div style={{ display: "flex", gap: 9, flexWrap: "wrap" }}>{children}</div>
}

export function Aviso({ children }: { children: React.ReactNode }) {
  return (
    <p style={{
      margin: "0 0 10px", padding: "7px 10px", borderRadius: 5, fontSize: 11.5,
      lineHeight: 1.5, background: "var(--aviso-soft, #fdf1d8)",
      color: "var(--aviso-on-soft, #7a4b00)", maxWidth: "84ch",
    }}>
      {children}
    </p>
  )
}

export function Vazio({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ padding: 24, maxWidth: "60ch" }}>
      <p style={{ margin: 0, fontSize: 12.5, color: "var(--txt3)", lineHeight: 1.6 }}>{children}</p>
    </div>
  )
}

/**
 * O estado de publicação e o botão que o troca.
 *
 * Um controle só para os quatro módulos, e **o mesmo texto em todos**: quem
 * publica um torque e uma caixa de fusíveis na mesma tarde não deveria ter de
 * decifrar duas telas.
 *
 * **Diz o que acontece antes de acontecer.** "No ar" e "fora do ar" são o
 * estado; a frase ao lado é a consequência — quem paga vê, ou deixa de ver.
 * Cadastrar sem publicar é o erro caro do módulo: o trabalho fica pronto e
 * invisível, e nada na tela denuncia.
 */
export function EstadoDePublicacao({ publicado, oQue, ocupado, aoTrocar }: {
  publicado: boolean
  /** O substantivo, para a frase sair em português: "a caixa", "o torque". */
  oQue: string
  ocupado?: boolean
  aoTrocar: (publicar: boolean) => void
}) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap",
      padding: "7px 10px", borderRadius: 6, marginBottom: 12,
      border: "1px solid " + (publicado ? "var(--ok, #2e7d32)" : "var(--line)"),
    }}>
      <span style={{
        fontSize: 11, fontWeight: 600,
        color: publicado ? "var(--ok, #2e7d32)" : "var(--txt3)",
      }}>
        {publicado ? "● no ar" : "○ fora do ar"}
      </span>

      <span style={{ fontSize: 11, color: "var(--txt2)", lineHeight: 1.5 }}>
        {publicado
          ? `Quem paga está vendo ${oQue}.`
          : `${oQue[0]!.toUpperCase()}${oQue.slice(1)} está pronto e invisível — ninguém vê.`}
      </span>

      <span style={{ marginLeft: "auto" }}>
        <Botao
          destaque={!publicado}
          ocupado={ocupado}
          aoClicar={() => aoTrocar(!publicado)}
          titulo={publicado
            ? "Tira do ar sem apagar nada: o cadastro continua."
            : "Faz aparecer para quem paga."}
        >
          {publicado ? "Tirar do ar" : "Publicar"}
        </Botao>
      </span>
    </div>
  )
}
